/**
 * What each record affordance requires, named once so every surface asking the same question shares
 * one rule. A rule whose role arm reads a fact only the call site holds — an ownership or assignment
 * check — is built there instead, since no constant can close over it.
 */
import { FeatureFlagsEnum } from '@omnea-app/packages/universal/feature-flags/featureFlags'
import { recordAuthorization } from './accessRules'
import { orgFlag } from './availability'
import { addsLibraryDocuments, creates, managesPermissions, viewsAllRequests } from './fgaRules'
import {
  adminsOnly,
  everyRole,
  everyRoleExceptSuppliers,
  noRole,
  nonViewerAdmins,
  nonViewerAdminsUnlessP2pRestricted,
  nonViewOnly,
  superAdmin,
} from './roleAuthorization'
import type { NonRecordAccessRule, RecordAccessRule } from './accessRules'

export const EDIT_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEdit', nonViewerAdmins),
}

/** Renaming, and reparenting from the hierarchy: open to every role before the service answered. */
export const EDIT_AGREEMENT_ANY_ROLE: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEdit', everyRole),
}

export const CANCEL_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canCancel', nonViewerAdmins),
}

export const DELETE_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canDelete', nonViewerAdmins),
}

/** Administering an agreement — today, marking it confidential. */
export const ADMINISTER_AGREEMENT: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canAdminister', noRole),
}

export const EDIT_AGREEMENT_DOCUMENTS: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canEditDocuments', nonViewOnly),
}

export const EDIT_REQUEST_METADATA: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', nonViewerAdmins),
}

/**
 * Wider than `EDIT_REQUEST_METADATA` on the same verb by a view-only administrator: the renewals
 * service admits one assigned to the renewal, and no assignment is visible here to narrow it by.
 */
export const EDIT_REQUEST_RENEWAL_DATE: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditMetadata', adminsOnly),
}

export const EDIT_REQUEST_PO_NUMBER: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditPoNumber', adminsOnly),
}

export const EDIT_REQUEST_SUBMISSIONS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditSubmissions', nonViewerAdmins),
}

export const REJECT_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canReject', nonViewerAdmins),
}

export const DELETE_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canDelete', nonViewerAdminsUnlessP2pRestricted),
}

export const REOPEN_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canRollback', nonViewerAdmins),
}

export const TOGGLE_REQUEST_HIDDEN_STEPS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canAdminister', adminsOnly),
}

/** Adding documents, editing their details, and adding or reverting versions. */
export const EDIT_REQUEST_DOCUMENTS: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canEditDocuments', nonViewOnly),
}

export const DELETE_REQUEST_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageDocuments', everyRoleExceptSuppliers),
}

export const MANAGE_SOURCING: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageSourcing', everyRole),
}

export const MANAGE_REQUEST_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('request', 'canManageTprm', nonViewOnly),
}

/**
 * Narrower than the destination page's own rule: before the service answered, a request linked through
 * to its supplier and agreement for admins only.
 */
export const LINK_SUPPLIER_FROM_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canView', adminsOnly),
}

export const LINK_AGREEMENT_FROM_REQUEST: RecordAccessRule = {
  authorization: recordAuthorization('agreement', 'canView', adminsOnly),
}

/** Naming a product that does not exist yet, which creates it on the chosen supplier. */
export const CREATE_SUPPLIER_PRODUCT: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canCreateProduct', everyRole),
}

export const VIEW_SUPPLIER_SUBMISSIONS: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canViewRelatedSubmissions', everyRole),
}

export const VIEW_SUPPLIER_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
}

export const MANAGE_SUPPLIER_TPRM: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canManageTprm', nonViewOnly),
}

/** The same verb, narrowed to organisations that have the screening module the data comes from. */
export const VIEW_SUPPLIER_SCREENING_CASES: RecordAccessRule = {
  availability: orgFlag(FeatureFlagsEnum.TPRM_CONTINUOUS_MONITORING),
  authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
}

export const VIEW_SUPPLIER_SUBPROCESSORS: RecordAccessRule = {
  availability: orgFlag(FeatureFlagsEnum.SUPPLIER_SUBPROCESSORS),
  authorization: recordAuthorization('supplier', 'canViewTprm', everyRole),
}

/** Administering a supplier — its lifecycle settings (archive, active/inactive, merge) and confidentiality. */
export const ADMINISTER_SUPPLIER: RecordAccessRule = {
  authorization: recordAuthorization('supplier', 'canAdminister', superAdmin),
}

export const CAN_ACCEPT_BILL: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canAccept', nonViewerAdmins),
}

export const CAN_APPROVE_BILL: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canApprove', nonViewerAdmins),
}

export const CAN_EDIT_BILL_BEFORE_ACCEPT: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canEditBeforeAccept', nonViewerAdmins),
}

export const CAN_ASSIGN_BILL: RecordAccessRule = {
  authorization: recordAuthorization('bill', 'canAssign', nonViewerAdmins),
}

/**
 * The contract email send admits acting admins by role and checks everyone else's request participation,
 * whatever the Permissions Service grants, so this asks the role alone; call sites add participation.
 */
export const SEND_CONTRACT_EMAIL_AS_ADMIN: NonRecordAccessRule = {
  authorization: { kind: 'role', role: nonViewerAdmins },
}

/** The unassign route admits admins by role, and no bill verb answers it, so this asks the role alone. */
export const UNASSIGN_BILL: NonRecordAccessRule = {
  authorization: { kind: 'role', role: nonViewerAdmins },
}

/** Permissions administrators may manage any record's access, so this asks the organisation, not the record. */
export const MANAGE_RECORD_ACCESS: NonRecordAccessRule = {
  authorization: { kind: 'fga', fga: managesPermissions },
}

/** A form's amendment request export holds reviewers' comments from every request, so it needs view access to all of them. */
export const EXPORT_AMENDMENT_REQUESTS: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: viewsAllRequests, role: adminsOnly },
}

export const CREATE_AGREEMENT: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: creates('agreement'), role: adminsOnly },
}

/** Rename, retype or edit the custom fields of one listed document. */
export const EDIT_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('document', 'canEdit', nonViewOnly),
}

/** Delete one listed document. */
export const MANAGE_DOCUMENT: RecordAccessRule = {
  authorization: recordAuthorization('document', 'canManage', nonViewOnly),
}

/**
 * Mark one document confidential and change who it is shared with. Nobody off FGA: the role rules ignore a document's
 * own mark and its access list, so offering either would promise a restriction nothing enforces.
 */
export const MANAGE_DOCUMENT_ACCESS: RecordAccessRule = {
  authorization: recordAuthorization('document', 'canManage', noRole),
}

/** Add new on the Documents page: a document with no parent needs the organisation documents level at edit. */
export const ADD_LIBRARY_DOCUMENT: NonRecordAccessRule = {
  authorization: { kind: 'fgaOrRole', fga: addsLibraryDocuments, role: nonViewerAdmins },
}
